import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  IconButton,
  FormControlLabel,
  Switch,
  Paper,
  Divider,
  Alert,
  Chip,
  Container,
  Snackbar,
  CircularProgress,
  useTheme,
  Tooltip,
  Backdrop
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  MusicNote as MusicNoteIcon,
  Save as SaveIcon,
  Audiotrack as AudiotrackIcon
} from '@mui/icons-material';
import useApiUrls from '../../useApiUrls';

const AddAudioListPackage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const theme = useTheme();
  const { getUrlByOperation, error: urlError } = useApiUrls();

  // State for handling multiple audio uploads
  const [audioFiles, setAudioFiles] = useState([]);
  
  // UI states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', type: 'success' });
  const [uploadProgress, setUploadProgress] = useState(0);
  const [packageInfo, setPackageInfo] = useState(null);
  
  // Fetch package info
  // useEffect(() => {
  //   const fetchPackageInfo = async () => {
  //     try {
  //       const token = getCookie("token");
  //       const userid = getCookie("userid");
        
  //       if (!token || !userid) return;
        
  //       const response = await axios.get(
  //         `https://api.dqr-display-tms.bonrix.in/api/AudioPackOperation/GetById?id=${id}`,
          
  //         {
  //           headers: {
  //             'X-Authorization': token,
  //             'UserId': userid
  //           }
  //         }
  //       );
        
  //       if (response.data && response.data.IsSuccess) {
  //         setPackageInfo(response.data.Data);
  //       }
  //     } catch (error) {
  //       console.error('Error fetching package info:', error);
  //     }
  //   };
    
  //   if (id) {
  //     fetchPackageInfo();
  //   }
  // }, [id]);

  // Handle file selection
  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    
    // Process each file and add it to the state
    const newAudioFiles = files.map(file => ({
      file,
      name: file.name.replace(/\.[^/.]+$/, ""), // Use filename without extension as default name
      remark: '',
      active: true,
      id: Date.now() + Math.random().toString(36).substring(2)  // Generate unique ID
    }));
    
    setAudioFiles(prev => [...prev, ...newAudioFiles]);
  };

  // Handle form input changes for a specific audio file
  const handleAudioDataChange = (id, field, value) => {
    setAudioFiles(prevFiles => 
      prevFiles.map(audio => 
        audio.id === id ? { ...audio, [field]: value } : audio
      )
    );
  };

  // Remove an audio file from the list
  const handleRemoveAudio = (id) => {
    setAudioFiles(prevFiles => prevFiles.filter(audio => audio.id !== id));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if there are any files to upload
    if (audioFiles.length === 0) {
      setNotification({
        open: true,
        message: 'Please select at least one audio file to upload',
        type: 'error'
      });
      return;
    }
    
    // Check if all audio files have names
    const missingNames = audioFiles.filter(audio => !audio.name.trim());
    if (missingNames.length > 0) {
      setNotification({
        open: true,
        message: 'Please provide a name for all audio files',
        type: 'error'
      });
      return;
    }
    
    setIsSubmitting(true);
    
    const token = getCookie("token");
    const userid = getCookie("userid");
    
    if (!token || !userid) {
      setNotification({
        open: true,
        message: 'Authentication failed. Please log in again.',
        type: 'error'
      });
      setIsSubmitting(false);
      return;
    }
    
    let successCount = 0;
    const totalFiles = audioFiles.length;
    
    // Use sequential upload instead of batch since the API expects a specific format
    for (let i = 0; i < totalFiles; i++) {
      const audio = audioFiles[i];
      const formData = new FormData();
      
      // IMPORTANT: Order matters for some APIs, place the file field last
      formData.append('AudioPackId', id);
      formData.append('Name', audio.name);
      formData.append('Remark', audio.remark || '');
      formData.append('Active', audio.active.toString());
      formData.append('File', audio.file);
      
      try {
        // Use the API URL from useApiUrls if available
        const apiUrl ='https://api.dqr-display-tms.bonrix.in/api/AudioPackInfoOperation/Insert';
        
        // Create a FormData object exactly matching the curl command structure
        const formData = new FormData();
        formData.append('AudioPackId', id);
        formData.append('Remark', audio.remark || '');
        formData.append('Active', 'true');  // Always set as string 'true'
        formData.append('Name', audio.name);
        
        // Add file with mime type explicitly specified
        const fileBlob = new Blob([audio.file], { type: audio.file.type });
        formData.append('File', fileBlob, audio.file.name);
        
        const response = await axios({
          method: 'post',
          url: apiUrl,
          data: formData,
          headers: {
            'accept': 'text/plain',
            'UserId': userid,
            'X-Authorization': token,
            'Content-Type': 'multipart/form-data'
          },
          transformRequest: [(data, headers) => {
            // Let axios handle the data transformation
            return data;
          }],
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            }
        });
        
        if (response.data && response.data.isresponse) {
          successCount++;
          // Update progress percentage
          setUploadProgress(Math.round((successCount * 100) / totalFiles));
        } else {
          console.error(`Error response for ${audio.name}:`, response.data);
        }
        
      } catch (error) {
        console.error(`Error uploading file ${audio.name}:`, error);
        
        // Log more detailed error information
        if (error.response) {
          console.error('Error response data:', error.response.data);
          console.error('Error response status:', error.response.status);
        }
      }
    }
    
    setIsSubmitting(false);
    
    if (successCount === totalFiles) {
      setNotification({
        open: true,
        message: 'All audio files uploaded successfully!',
        type: 'success'
      });
      
      setTimeout(() => {
        navigate(`/audioListPackage/${id}`);
      }, 2000);
    } else if (successCount > 0) {
      setNotification({
        open: true,
        message: `${successCount} out of ${totalFiles} audio files uploaded successfully.`,
        type: 'warning'
      });
    } else {
      setNotification({
        open: true,
        message: 'Failed to upload audio files. Please try again.',
        type: 'error'
      });
    }
  };

  // Handle close notification
  const handleCloseNotification = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setNotification({ ...notification, open: false });
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ 
          p: 2, 
          bgcolor: theme.palette.primary.main, 
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AudiotrackIcon sx={{ mr: 1 }} />
            <Typography variant="h6" component="h1">
              Add Multiple Audio Files
            </Typography>
          </Box>
          <Tooltip title="Back to Audio List">
            <IconButton 
              color="inherit" 
              onClick={() => navigate(`/audioListPackage/${id}`)}
              size="small"
            >
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
        </Box>
        
        {packageInfo && (
          <Box sx={{ px: 3, py: 2, bgcolor: theme.palette.grey[50] }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Package: {packageInfo.Name}
            </Typography>
          </Box>
        )}
        
        <form onSubmit={handleSubmit}>
          <Box sx={{ p: 3 }}>
            {/* File Upload Section */}
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 3, 
                mb: 4, 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                border: `2px dashed ${theme.palette.primary.main}`,
                borderRadius: 2,
                bgcolor: theme.palette.grey[50],
                '&:hover': {
                  bgcolor: theme.palette.grey[100],
                  cursor: 'pointer'
                }
              }}
              onClick={() => document.getElementById('file-input').click()}
            >
              <input
                type="file"
                id="file-input"
                multiple
                accept="audio/*"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              <CloudUploadIcon color="primary" sx={{ fontSize: 48, mb: 2 }} />
              <Typography variant="h6" color="primary" gutterBottom>
                Upload Audio Files
              </Typography>
              <Typography variant="body2" color="textSecondary" align="center">
                Click or drag and drop audio files here<br />
                You can upload multiple files at once
              </Typography>
              <Button 
                variant="contained" 
                color="primary"
                startIcon={<AddIcon />}
                sx={{ mt: 2 }}
                onClick={(e) => {
                  e.stopPropagation();
                  document.getElementById('file-input').click();
                }}
              >
                Select Files
              </Button>
            </Paper>
            
            {/* Files Preview & Details */}
            {audioFiles.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <MusicNoteIcon sx={{ mr: 1 }} />
                  Audio Files ({audioFiles.length})
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                {audioFiles.map((audio, index) => (
                  <Paper 
                    key={audio.id} 
                    variant="outlined"
                    sx={{ 
                      mb: 2, 
                      p: 2, 
                      position: 'relative',
                      borderRadius: 2
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <MusicNoteIcon color="primary" sx={{ mr: 1 }} />
                          <Typography variant="subtitle1" sx={{ flex: 1 }}>
                            File {index + 1}: {audio.file.name}
                          </Typography>
                          <Chip 
                            label={`${(audio.file.size / 1024 / 1024).toFixed(2)} MB`}
                            size="small"
                            sx={{ mr: 1 }}
                          />
                          <IconButton 
                            color="error" 
                            onClick={() => handleRemoveAudio(audio.id)}
                            disabled={isSubmitting}
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Audio Name"
                          fullWidth
                          required
                          value={audio.name}
                          onChange={(e) => handleAudioDataChange(audio.id, 'name', e.target.value)}
                          disabled={isSubmitting}
                          size="small"
                          placeholder="Enter a descriptive name"
                        />
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Remarks"
                          fullWidth
                          value={audio.remark}
                          onChange={(e) => handleAudioDataChange(audio.id, 'remark', e.target.value)}
                          disabled={isSubmitting}
                          size="small"
                          placeholder="Optional notes about this audio"
                        />
                      </Grid>
                      
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={audio.active}
                              onChange={(e) => handleAudioDataChange(audio.id, 'active', e.target.checked)}
                              disabled={isSubmitting}
                            />
                          }
                          label="Active"
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </Box>
            )}
            
            {/* Upload Progress */}
            {isSubmitting && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Uploading {audioFiles.length} files...
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: '100%', mr: 1 }}>
                    <LinearProgress variant="determinate" value={uploadProgress} />
                  </Box>
                  <Box sx={{ minWidth: 35 }}>
                    <Typography variant="body2" color="textSecondary">
                      {`${uploadProgress}%`}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
            
            {/* Submit Button */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                variant="outlined"
                onClick={() => navigate(`/audioListPackage/${id}`)}
                startIcon={<ArrowBackIcon />}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSubmitting || audioFiles.length === 0}
                startIcon={isSubmitting ? <CircularProgress size={20} /> : <SaveIcon />}
              >
                {isSubmitting ? 'Uploading...' : `Upload ${audioFiles.length} Files`}
              </Button>
            </Box>
          </Box>
        </form>
      </Paper>
      
      {/* Notifications */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.type} 
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
      
      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isSubmitting}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <CircularProgress color="inherit" />
          <Typography sx={{ mt: 2 }}>Uploading {audioFiles.length} audio files...</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {uploadProgress}% complete
          </Typography>
        </Box>
      </Backdrop>
    </Container>
  );
};

// Helper function to get cookies
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}

// Helper Linear Progress component
const LinearProgress = ({ variant, value }) => {
  const theme = useTheme();
  
  return (
    <Box sx={{ 
      height: 8, 
      width: '100%', 
      bgcolor: theme.palette.grey[200],
      borderRadius: 5,
      position: 'relative',
      overflow: 'hidden'
    }}>
      <Box sx={{ 
        width: `${value}%`, 
        bgcolor: theme.palette.primary.main,
        position: 'absolute',
        top: 0,
        left: 0,
        bottom: 0,
        transition: 'width 0.3s ease-in-out',
        borderRadius: 5
      }} />
    </Box>
  );
};

export default AddAudioListPackage;